import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/core/alert-dialog';
import { Button } from '@/components/core/button';
import { Result } from '@/shared/helpers/Result';
import { useOptionallyControlledState } from '@/shared/hooks/useOptionallyControlledState';

type ContextValue = {
  loading: boolean;
  onAction: (event: React.MouseEvent) => Promise<void>;
};

const initialContextValue: ContextValue = {
  loading: false,
  onAction: async () => {
    throw new Error('onAction not initialized');
  },
};

const AlertDialogWithActionContext = createContext<ContextValue>(initialContextValue);

const useAlertDialogActionContext = () => useContext(AlertDialogWithActionContext);

function AlertDialogWithAction({
  onAction,
  children,
  open,
  onOpenChange,
  ...props
}: Omit<ComponentProps<typeof AlertDialog>, 'open' | 'onOpenChange'> & {
  onAction: () => Promise<Result>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [isOpen, setIsOpen] = useOptionallyControlledState({
    default: false,
    controlled: open,
    setControlled: onOpenChange,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
    }
  }, [isOpen]);

  const handleAction = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();
      // Radix keeps a closing dialog mounted and interactive until its exit animation
      // finishes, and a successful action re-enables the button before that. A click
      // landing in that window must not re-run the action.
      if (!isOpen) return;
      setLoading(true);

      try {
        const result = await onAction();
        if (result.isSuccess) {
          setIsOpen(false);
        }
      } finally {
        setLoading(false);
      }
    },
    [isOpen, onAction, setIsOpen],
  );

  const contextValue = useMemo(
    () => ({
      onAction: handleAction,
      loading,
    }),
    [handleAction, loading],
  );

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen} {...props}>
      <AlertDialogWithActionContext.Provider value={contextValue}>
        {children}
      </AlertDialogWithActionContext.Provider>
    </AlertDialog>
  );
}

function AlertDialogWithActionSubmit({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogAction>) {
  const { loading, onAction } = useAlertDialogActionContext();
  return (
    <AlertDialogAction className={className} asChild {...props}>
      <Button loading={loading} onClick={onAction}>
        {props.children}
      </Button>
    </AlertDialogAction>
  );
}

function AlertDialogWithActionCancel({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogCancel>) {
  const { loading } = useAlertDialogActionContext();
  return <AlertDialogCancel className={className} disabled={loading} {...props} />;
}

export {
  AlertDialogWithAction,
  AlertDialogTrigger as AlertDialogWithActionTrigger,
  AlertDialogContent as AlertDialogWithActionContent,
  AlertDialogHeader as AlertDialogWithActionHeader,
  AlertDialogFooter as AlertDialogWithActionFooter,
  AlertDialogTitle as AlertDialogWithActionTitle,
  AlertDialogDescription as AlertDialogWithActionDescription,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionCancel,
};
